import {
  Shield,
  Star,
  Eye,
  Award,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { GoogleReviews } from "./GoogleReviews";

export function HomePage() {
  const features = [
    {
      icon: Shield,
      title: "Safe & Effective",
      description:
          "We use eco-friendly methods that are safe for your family and pets while being highly effective against pests.",
    },
    {
      icon: Award,
      title: "Licensed & Insured",
      description:
          "Fully licensed pest control professionals with comprehensive insurance coverage for your peace of mind.",
    },
    {
      icon: Star,
      title: "Guaranteed Results",
      description:
          "We stand behind our work with a satisfaction guarantee.",
    },
  ];

  const offers = [
    {
      icon: Eye,
      title: "Free Visual Inspection",
      description:
          "Complete property assessment to identify pest issues and entry points at no cost to you.",
      highlight: "Always Free",
    }
  ];

  return (
      <div className="min-h-screen">
        <Helmet>
          <title>BugBuddies Pest Solutions | Pest Control Experts</title>
          <meta name="description" content="Professional, reliable, and eco-friendly pest control services across Saskatchewan." />
          <link rel="canonical" href="https://bugbuddies.ca/" />
        </Helmet>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#075123] to-[#05401A] text-white py-20 px-[0px] py-[20px] p-[0px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-[0px] py-[10px]">
              <div>
                <h1 className="text-4xl md:text-6xl font-bold mb-4">
                  Your Trusted Pest Control Experts
                </h1>
                <p className="text-2xl md:text-3xl font-bold text-green-200 mb-6 italic">
                  "They can run... But they can't hide!"
                </p>
                <p className="text-xl mb-8 text-green-100">
                  Professional, reliable, and eco-friendly pest
                  control services. We eliminate pests and keep
                  them away for good.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                      size="lg"
                      className="bg-white text-[#075123] hover:bg-gray-100"
                      asChild
                  >
                    <Link to="/contact">Get Free Visual Inspection</Link>
                  </Button>
                  <Button
                      size="lg"
                      variant="outline"
                      className="border-white text-[#075123] hover:bg-white hover:text-[#075123]"
                      asChild
                  >
                    <Link to="/services">Our Services</Link>
                  </Button>
                </div>
              </div>
              <div className="relative flex items-center justify-center">
                <ImageWithFallback
                    src="/100-satisfaction.png"
                    alt="Satisfaction guaranteed badge"
                    className="w-full max-w-[420px] h-auto object-contain
               drop-shadow-[0_18px_48px_rgba(0,0,0,0.45)]
               brightness-110 contrast-125"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Special Offers Section */}
        <section className="py-16 bg-yellow-50 border-y-4 border-yellow-400 px-[0px] py-[20px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Special Offers
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Take advantage of our current promotions and save
                on professional services.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 max-w-4xl mx-auto justify-items-center">
              {offers.map((offer, index) => (
                  <Card
                      key={index}
                      className="w-full md:w-2/3 border-2 border-yellow-400 bg-white shadow-lg"
                  >
                    <CardContent className="p-8 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-full mb-6">
                        <offer.icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="inline-block bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold mb-4">
                        {offer.highlight}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {offer.title}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {offer.description}
                      </p>
                      <Button
                          size="lg"
                          className="bg-[#075123] hover:bg-[#05401A]"
                          asChild
                      >
                        <Link to="/contact">Get Quote</Link>
                      </Button>
                    </CardContent>
                  </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose BugBuddies?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We're committed to providing the highest quality
                pest control services with a focus on safety,
                effectiveness, and customer satisfaction.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                  <Card
                      key={index}
                      className="text-center p-6 h-full"
                  >
                    <CardContent className="pt-6">
                      <feature.icon className="h-12 w-12 text-[#075123] mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Google Reviews Section */}
        <GoogleReviews />

        {/* CTA Section */}
        <section className="py-16 bg-[#075123] text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Rid of Pests?
            </h2>
            <p className="text-xl mb-6 text-green-100">
              Don't let pests take over your home. Contact us
              today for your free visual inspection and take the
              first step towards a pest-free environment.
            </p>
            <div className="bg-[#05401A] rounded-lg p-6 mb-8 inline-block">
              <p className="text-lg font-semibold mb-2">
                <a href="tel:3062017883" className="hover:text-yellow-300">
                  📞 Call Now: (306) 201-7883
                </a>
              </p>
              <p className="text-green-100 mb-2 font-bold">
                To get a quote
              </p>
              <p className="text-lg font-semibold mb-2">
                <a href="tel:3065371423" className="hover:text-yellow-300">
                  📞 Call Now: (306) 537-1423
                </a>
              </p>
            </div>
            <div>
              <Button
                  size="lg"
                  className="bg-white text-[#075123] hover:bg-gray-100"
                  asChild
              >
                <Link to="/contact">Schedule Free Visual Inspection</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
  );
}
